import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { LineChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { motion } from 'framer-motion';
import { TrendingUp } from 'lucide-react';

const TrendForecast = ({ filters, reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [forecastData, setForecastData] = useState({ historical: [], forecast: [], meta: {} });
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState('connection_rate');
  const animationDuration = reducedMotion ? 0 : 0.6;

  useEffect(() => {
    fetchForecastData();
  }, [filters, metric]);

  const fetchForecastData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/insights/v2/forecast?metric=${metric}&horizon_hours=24`);
      const result = await response.json();
      setForecastData(result);
    } catch (error) {
      console.error('Error fetching forecast:', error);
    } finally {
      setLoading(false);
    }
  };

  const combined = [
    ...forecastData.historical.map(h => ({ ...h, type: 'historical' })),
    ...forecastData.forecast.map(f => ({ timestamp: f.timestamp, value: f.predicted_value, type: 'forecast', confidence_lower: f.confidence_lower, confidence_upper: f.confidence_upper }))
  ];

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <TrendingUp className="w-5 h-5 text-green-400" />
          Trend Forecast (24h Ahead)
        </CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          Statistical predictions with confidence intervals
        </p>
      </CardHeader>
      <CardContent>
        {/* Metric Toggle */}
        <div className="flex gap-2 mb-6">
          {[
            { key: 'connection_rate', label: 'Connection Rate' },
            { key: 'api_latency', label: 'API Latency' },
            { key: 'asr_wer', label: 'ASR WER' }
          ].map((m) => (
            <button
              key={m.key}
              onClick={() => setMetric(m.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                metric === m.key
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-pulse text-slate-400">Generating forecast...</div>
          </div>
        ) : combined.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <p>No data available for forecasting</p>
          </div>
        ) : (
          <motion.div
            initial={reducedMotion ? {} : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: animationDuration }}
          >
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={combined} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="timestamp" stroke="#94a3b8" fontSize={11} tickFormatter={formatTime} />
                <YAxis stroke="#94a3b8" fontSize={11} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                  labelStyle={{ color: '#e2e8f0' }}
                />
                <Legend />
                
                {/* Historical Line */}
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#C9A24A"
                  strokeWidth={2}
                  dot={{ fill: '#C9A24A', r: 3 }}
                  name="Historical"
                  animationDuration={reducedMotion ? 0 : 1000}
                />
                
                {/* Confidence Band */}
                <Area
                  type="monotone"
                  dataKey="confidence_upper"
                  fill="#10b981"
                  fillOpacity={0.1}
                  stroke="none"
                  name="Confidence"
                />
                <Area
                  type="monotone"
                  dataKey="confidence_lower"
                  fill="#10b981"
                  fillOpacity={0.1}
                  stroke="none"
                />
                
                {/* Forecast Line */}
                <Line
                  type="monotone"
                  dataKey={(d) => d.type === 'forecast' ? d.value : null}
                  stroke="#10b981"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: '#10b981', r: 3 }}
                  name="Forecast"
                  animationDuration={reducedMotion ? 0 : 1000}
                />
                
                {/* Now Line */}
                {forecastData.historical.length > 0 && (
                  <ReferenceLine
                    x={forecastData.historical[forecastData.historical.length - 1].timestamp}
                    stroke="#fbbf24"
                    strokeDasharray="3 3"
                    label={{ value: 'Now', fill: '#fbbf24', fontSize: 11 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>

            {/* Stats */}
            {forecastData.meta && (
              <div className="mt-6 grid grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gold">{forecastData.historical.length}</p>
                  <p className="text-xs text-slate-400 mt-1">Historical Points</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-400">{forecastData.forecast.length}</p>
                  <p className="text-xs text-slate-400 mt-1">Predicted Hours</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-400">{forecastData.meta.sample_size}</p>
                  <p className="text-xs text-slate-400 mt-1">Calls Analyzed</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-400">
                    {forecastData.forecast.length > 0 ? (forecastData.forecast[0].confidence_level * 100).toFixed(0) : 0}%
                  </p>
                  <p className="text-xs text-slate-400 mt-1">Confidence</p>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
};

export default TrendForecast;
